// Small inline SVG icon set (no icon library). Icons are decorative by default
// (aria-hidden); the surrounding control provides the accessible name.
import type { ReactNode, SVGProps } from "react";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "children"> & { size?: number };

function make(paths: ReactNode, displayName: string) {
  function Icon({ size = 18, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {paths}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const LogoIcon = make(
  <>
    <path d="M12 2.5 20.5 7.4v9.2L12 21.5 3.5 16.6V7.4z" />
    <path d="M3.5 7.4 12 12.3l8.5-4.9M12 12.3v9.2" />
  </>,
  "LogoIcon",
);
export const HomeIcon = make(<path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />, "HomeIcon");
export const FolderIcon = make(<path d="M3 6.5A1.5 1.5 0 0 1 4.5 5H9l2 2.5h8.5A1.5 1.5 0 0 1 21 9v9.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z" />, "FolderIcon");
export const PlayCircleIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m10 8.5 5 3.5-5 3.5z" />
  </>,
  "PlayCircleIcon",
);
export const FileTextIcon = make(
  <>
    <path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z" />
    <path d="M14 3v5h5M8.5 13h7M8.5 16.5h7" />
  </>,
  "FileTextIcon",
);
export const BookIcon = make(
  <>
    <path d="M12 6.5C10 5 7 4.5 3.5 5v13c3.5-.5 6.5 0 8.5 1.5 2-1.5 5-2 8.5-1.5V5C17 4.5 14 5 12 6.5z" />
    <path d="M12 6.5v13" />
  </>,
  "BookIcon",
);
export const SettingsIcon = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </>,
  "SettingsIcon",
);
export const ActivityIcon = make(<path d="M3 12h4l3-8 4 16 3-8h4" />, "ActivityIcon");
export const PlugIcon = make(
  <>
    <path d="M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-12 0zM12 17v5" />
  </>,
  "PlugIcon",
);
export const InboxIcon = make(
  <>
    <path d="M3 13h5l1.5 3h5L16 13h5" />
    <path d="M5.5 5h13L21 13v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z" />
  </>,
  "InboxIcon",
);
export const CpuIcon = make(
  <>
    <rect x="6" y="6" width="12" height="12" rx="1.5" />
    <path d="M9.5 9.5h5v5h-5zM9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
  </>,
  "CpuIcon",
);
export const SearchIcon = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </>,
  "SearchIcon",
);
export const PlusIcon = make(<path d="M12 5v14M5 12h14" />, "PlusIcon");
export const PaperclipIcon = make(
  <path d="m21 11.5-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />,
  "PaperclipIcon",
);
export const SendIcon = make(<path d="M21 3 10 14M21 3l-7 18-4-7-7-4z" />, "SendIcon");
export const GridIcon = make(
  <>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
  </>,
  "GridIcon",
);
export const ListIcon = make(<path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />, "ListIcon");
export const ChevronDownIcon = make(<path d="m6 9 6 6 6-6" />, "ChevronDownIcon");
export const ChevronUpIcon = make(<path d="m6 15 6-6 6 6" />, "ChevronUpIcon");
export const ChevronRightIcon = make(<path d="m9 6 6 6-6 6" />, "ChevronRightIcon");
export const ArrowRightIcon = make(<path d="M5 12h14M13 6l6 6-6 6" />, "ArrowRightIcon");
export const CheckIcon = make(<path d="m5 12.5 4.5 4.5L19 7.5" />, "CheckIcon");
export const CheckCircleIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12.3 2.8 2.7L16 9.5" />
  </>,
  "CheckCircleIcon",
);
export const XIcon = make(<path d="M6 6l12 12M18 6 6 18" />, "XIcon");
export const XCircleIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </>,
  "XCircleIcon",
);
export const AlertTriangleIcon = make(
  <>
    <path d="M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4.5M12 17h.01" />
  </>,
  "AlertTriangleIcon",
);
export const InfoIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 7.5h.01" />
  </>,
  "InfoIcon",
);
export const ClockIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
  "ClockIcon",
);
export const CircleIcon = make(<circle cx="12" cy="12" r="8.5" />, "CircleIcon");
export const LoaderIcon = make(<path d="M12 3a9 9 0 1 0 9 9" />, "LoaderIcon");
export const PauseIcon = make(<path d="M9 5v14M15 5v14" />, "PauseIcon");
export const PauseCircleIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9v6M14 9v6" />
  </>,
  "PauseCircleIcon",
);
export const PlayIcon = make(<path d="M7 4.5v15l12-7.5z" />, "PlayIcon");
export const BanIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.7 5.7 12.6 12.6" />
  </>,
  "BanIcon",
);
export const LockIcon = make(
  <>
    <rect x="4.5" y="10.5" width="15" height="10" rx="1.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </>,
  "LockIcon",
);
export const ExternalLinkIcon = make(<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />, "ExternalLinkIcon");
export const MenuIcon = make(<path d="M4 6h16M4 12h16M4 18h16" />, "MenuIcon");
export const MoreIcon = make(<path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth={3} />, "MoreIcon");
export const GitBranchIcon = make(
  <>
    <circle cx="6" cy="5" r="2" />
    <circle cx="6" cy="19" r="2" />
    <circle cx="18" cy="7" r="2" />
    <path d="M6 7v10M18 9c0 5-6 4-12 8" />
  </>,
  "GitBranchIcon",
);
export const WrenchIcon = make(
  <path d="M14.7 6.3a4 4 0 0 0 5 5l-9.2 9.2a2.1 2.1 0 0 1-3-3l9.2-9.2a4 4 0 0 1-5-5l2.6 2.6 2.4-.2.2-2.4z" />,
  "WrenchIcon",
);
export const UserIcon = make(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </>,
  "UserIcon",
);
export const GlobeIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </>,
  "GlobeIcon",
);
export const RefreshIcon = make(<path d="M20 11a8 8 0 0 0-14.9-3.5M4 4v4h4M4 13a8 8 0 0 0 14.9 3.5M20 20v-4h-4" />, "RefreshIcon");
export const MessageIcon = make(<path d="M21 12a8.5 8.5 0 0 1-12.4 7.5L3 21l1.5-5.1A8.5 8.5 0 1 1 21 12z" />, "MessageIcon");
export const MonitorIcon = make(
  <>
    <rect x="3" y="4" width="18" height="12.5" rx="1.5" />
    <path d="M8 20.5h8M12 16.5v4" />
  </>,
  "MonitorIcon",
);
export const LogOutIcon = make(<path d="M9 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4M16 17l5-5-5-5M21 12H9" />, "LogOutIcon");
export const BuildingIcon = make(
  <>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01M10 21v-3h4v3" />
  </>,
  "BuildingIcon",
);
export const ServerIcon = make(
  <>
    <rect x="3.5" y="4" width="17" height="7" rx="1.5" />
    <rect x="3.5" y="13" width="17" height="7" rx="1.5" />
    <path d="M7.5 7.5h.01M7.5 16.5h.01" />
  </>,
  "ServerIcon",
);
export const KeyIcon = make(
  <>
    <circle cx="7.5" cy="15.5" r="4" />
    <path d="m10.5 12.5 9-9M16 6l2.5 2.5M14 8l2 2" />
  </>,
  "KeyIcon",
);
export const LayersIcon = make(<path d="m12 3 9 5-9 5-9-5zM3 13l9 5 9-5M3 17.5l9 5 9-5" />, "LayersIcon");
export const HistoryIcon = make(<path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3.5 2" />, "HistoryIcon");
export const ShieldIcon = make(<path d="M12 3 4.5 6v6c0 4.5 3.2 8 7.5 9 4.3-1 7.5-4.5 7.5-9V6z" />, "ShieldIcon");
export const GaugeIcon = make(<path d="M12 14l4-4M3.5 18a9.5 9.5 0 1 1 17 0" />, "GaugeIcon");
export const CodeIcon = make(<path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" />, "CodeIcon");
export const KanbanIcon = make(
  <>
    <rect x="3.5" y="4" width="17" height="16" rx="1.5" />
    <path d="M9.5 4v16M14.5 4v16" />
  </>,
  "KanbanIcon",
);
export const UploadIcon = make(<path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />, "UploadIcon");
export const WifiOffIcon = make(<path d="m3 3 18 18M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 5.5-2.8M19 13a10 10 0 0 0-2-1.6M2 8.8a15 15 0 0 1 4.2-2.6M22 8.8A15 15 0 0 0 11 5M12 20h.01" />, "WifiOffIcon");
export const LinkIcon = make(<path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2" />, "LinkIcon");
export const SparklesIcon = make(<path d="M12 3.5 13.8 9l5.7 1.8-5.7 1.8L12 18.5l-1.8-5.9-5.7-1.8L10.2 9zM19 3v3M17.5 4.5h3M5 17.5v3M3.5 19h3" />, "SparklesIcon");
export const PanelRightIcon = make(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M15 4v16" />
  </>,
  "PanelRightIcon",
);
